import { useRef, useState, type ChangeEvent } from 'react';
import { useGT } from 'gt-react';
import { useToast } from 'src/context/ToastContext';
import {
  PRICELIST_UPLOAD_ACCEPT,
  validatePricelistFile,
} from 'src/lib/pricelist-utils';
import { acceptDroppedFiles } from './components/fileDropzoneAccept';
import {
  caseUploadRejectionFeedback,
  initialPricelistUploadFiles,
  pricelistFilesToUpload,
} from './pricelistUploadFiles';

export function usePricelistUploadFiles({
  initialFile,
  initialFiles,
  caseMode,
}: {
  initialFile: File | null;
  initialFiles: readonly File[];
  caseMode: boolean;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(initialFile);
  const [selectedFiles, setSelectedFiles] = useState<
    { id: string; file: File }[]
  >(() =>
    initialPricelistUploadFiles(initialFile, initialFiles).map((file) => ({
      id: crypto.randomUUID(),
      file,
    })),
  );
  const [sizeError, setSizeError] = useState<string | null>(null);

  async function handleSingleFileChange(file: File) {
    const rejection = await validatePricelistFile(file, gt);
    if (rejection) {
      // Size errors render inline under the picker; type errors as a toast.
      if (rejection.kind === 'too-large') {
        setSizeError(rejection.message);
      } else {
        showToast({
          title: rejection.title,
          message: rejection.message,
          color: 'error',
        });
      }
      setSelectedFile(null);
      return;
    }
    setSizeError(null);
    setSelectedFile(file);
  }

  async function handleCaseFilesChange(files: readonly File[]) {
    const { accepted, rejections } = await acceptDroppedFiles(
      files,
      PRICELIST_UPLOAD_ACCEPT,
      (file) =>
        validatePricelistFile(file, gt, { acceptPrintablePlainText: true }),
      gt,
    );
    const { sizeError: nextSizeError, toasts } =
      caseUploadRejectionFeedback(rejections);
    for (const rejection of toasts) {
      showToast({
        title: rejection.title,
        message: rejection.message,
        color: 'error',
      });
    }
    if (nextSizeError !== null) {
      setSizeError(nextSizeError);
    } else {
      setSizeError(null);
    }
    if (accepted.length > 0) {
      setSelectedFiles((current) => [
        ...current,
        ...accepted.map((file) => ({ id: crypto.randomUUID(), file })),
      ]);
    }
  }

  async function chooseFiles(chosen: readonly File[]) {
    if (chosen.length === 0) return;
    if (caseMode) {
      await handleCaseFilesChange(chosen);
      return;
    }
    const file = chosen[0];
    if (!file) return;
    await handleSingleFileChange(file);
  }

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    await chooseFiles(Array.from(e.target.files ?? []));
    if (caseMode) e.target.value = '';
  }

  function removeSelectedFile(id: string) {
    setSizeError(null);
    setSelectedFiles((current) => current.filter((item) => item.id !== id));
  }

  const filesToUpload = pricelistFilesToUpload(
    caseMode,
    selectedFiles.map((item) => item.file),
    selectedFile,
  );

  return {
    fileInputRef,
    selectedFile,
    selectedFiles,
    sizeError,
    filesToUpload,
    chooseFiles,
    handleFileChange,
    removeSelectedFile,
  };
}
